import { authFetch } from '../auth/authFetch';
import { DraftSnapshot } from './draftStorage';
import type { components } from './schema.gen';

// Wire types are generated from the backend's OpenAPI schema (FastAPI/Pydantic is the
// single source of truth). Regenerate with `npm run gen:api` whenever the backend
// models change — CI fails if this file drifts. Do not hand-edit these shapes.
export type PostPayload = components['schemas']['PostPayload'];
export type UploadResult = components['schemas']['UploadOut'];

// The backend draft shadow is an opaque jsonb passthrough (typed `dict` server-side, so
// OpenAPI renders it as the useless `Record<string, never>`). It's a content-only subset
// of PostPayload — distinct from the local-storage DraftSnapshot (no `published`). Derive
// the FE's view from PostPayload so the content fields can't drift. See recoverDraft for
// how the backend shadow and the local snapshot are reconciled.
export type DraftShadow = Pick<
  PostPayload,
  'title' | 'excerpt' | 'featured_image_url' | 'body_html'
>;

export type Post = Omit<components['schemas']['AdminPostOut'], 'draft'> & {
  draft?: DraftShadow | null;
};

// Derive a short plain-text summary from rich-text body HTML, so authors don't
// write a separate excerpt. Strips tags/entities, collapses whitespace, and
// truncates at a word boundary with an ellipsis.
export function excerptFromHtml(html: string, maxLen = 160): string {
  const text = html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
  if (text.length <= maxLen) return text;
  const cut = text.slice(0, maxLen);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > 0 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}

// Count words in rich-text body HTML for the editor's word counter. Strips
// tags + entities, collapses whitespace; returns 0 for empty/whitespace-only.
export function countWords(html: string): number {
  const text = html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&[a-z]+;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!text) return 0;
  return text.split(' ').length;
}

// Admin listing: every post including drafts (the public /blog list is
// published-only). authFetch attaches the bearer token and refreshes on 401.
export async function listPosts(): Promise<Post[]> {
  const res = await authFetch('/clients/new_dawn/admin/posts');
  if (!res.ok) {
    throw new Error(`List posts failed: ${res.status}`);
  }
  return res.json();
}

export async function createPost(payload: PostPayload): Promise<Post> {
  const res = await authFetch('/clients/new_dawn/admin/posts', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error(`Create post failed: ${res.status}`);
  }
  return res.json();
}

export async function updatePost(id: number, payload: PostPayload): Promise<Post> {
  const res = await authFetch(`/clients/new_dawn/admin/posts/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error(`Update post failed: ${res.status}`);
  }
  return res.json();
}

// Autosave sends content only — never `published`, so an autosave can't publish or
// unpublish. The backend creates a hidden draft row and returns it with an id.
export async function createDraft(snapshot: DraftSnapshot): Promise<Post> {
  const res = await authFetch('/clients/new_dawn/admin/posts/draft', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      title: snapshot.title,
      featured_image_url: snapshot.featured_image_url ?? undefined,
      body_html: snapshot.body_html,
    }),
  });
  if (!res.ok) {
    throw new Error(`Create draft failed: ${res.status}`);
  }
  return res.json();
}

export async function autosavePost(id: number, snapshot: DraftSnapshot): Promise<Post> {
  const res = await authFetch(`/clients/new_dawn/admin/posts/${id}/autosave`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      title: snapshot.title,
      featured_image_url: snapshot.featured_image_url ?? undefined,
      body_html: snapshot.body_html,
    }),
  });
  if (!res.ok) {
    throw new Error(`Autosave failed: ${res.status}`);
  }
  return res.json();
}

export async function deletePost(id: number): Promise<void> {
  const res = await authFetch(`/clients/new_dawn/admin/posts/${id}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    throw new Error(`Delete post failed: ${res.status}`);
  }
}

export async function uploadImage(file: File): Promise<UploadResult> {
  const form = new FormData();
  form.append('file', file);
  const res = await authFetch('/clients/new_dawn/admin/uploads', {
    method: 'POST',
    // No Content-Type: the browser sets the multipart boundary automatically.
    body: form,
  });
  if (!res.ok) {
    throw new Error(`Image upload failed: ${res.status}`);
  }
  return res.json();
}
